import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

const STERILE_MATE_LOGO = "https://customer-assets-cm19k8pv.emergentagent.net/job_831e67a6-d823-415e-9bc6-ce18d6b738ca/artifacts/sxm1cszk_Sterile%20Mate%20Logo%20PNG.webp";
const STERILE_MATE_LOGO_WHITE = "https://customer-assets-cm19k8pv.emergentagent.net/job_831e67a6-d823-415e-9bc6-ce18d6b738ca/artifacts/73gmnnia_Sterile%20Mate%20Logo%20White%20%281%29.webp";

// =============================================================================
// SCENARIO CONFIGS — Plan A (Humble) and Plan B (263 N Sam Houston / "2SIXTY3")
// All building / address / use-of-funds copy that differs between the two plans
// is held here so the PDF body re-uses one render path.
// =============================================================================
const PUBLIC_BASE = (typeof window !== 'undefined' && window.location?.origin) || '';
const SCENARIOS = {
  ronan: {
    id: 'ronan',
    label: 'Plan C — 16651 Ronan Rd HQ',
    coverTitle: 'SBA 504 Business Plan — Plan C',
    coverImage: `${PUBLIC_BASE}/images/ronan_building/cover_aerial_branded.jpg`,
    coverFunds: '$4,490,000 — Real Estate Acquisition + Renovation + Marketing + LOC',
    coverAddress: '16651 Ronan Rd, Houston, TX 77060 — 22,750 SF on 5.08 Acres',
    address: '16651 Ronan Rd, Houston, TX 77060',
    sqft: '22,750 SF · 3 Stories · Class-A Corporate Office · 5.08 Acres',
    totalFunds: 4_490_000,
    totalLabel: '$4,490,000',
    monthlyDebt: '~$32,631',
    annualDebt: '$391,571',
    // DSCR reflects the seven diversified operating revenue streams (Backorder Sourcing flagship + six supporting) — see PrivateLabelGloves tab in the pro-forma.
    dscr: { y1: '3.33x', y2: '6.83x', y3: '11.57x' },
    operationsHeadline: 'Operations Plan — 16651 Ronan Rd Campus & Phases',
    operationsSub: '16651 Ronan Rd, Houston, TX 77060 — 22,750 SF · 3 Stories · Class-A Corporate Office on 5.08 Acres',
    operationsLead:
      'The 16651 Ronan Rd campus is a 22,750 SF Class-A 3-story corporate office building situated on 5.08 acres in the Greenspoint corridor, at the intersection of Beltway 8 and I-45. The existing brick-clad headquarters features a private top-floor executive suite, multiple conference rooms, a wellness room, and a move-in-ready interior with a formal BWS architect + MEP renovation program (~$160K) focused on the 2nd-Level executive layout. Sterile Mate is owner-occupied across all three floors: Floor 1 (10,438 SF) houses HQ, showroom, and training center; Floor 2 (12,640 SF) blends a 3,500 SF short-stock warehouse with the Texas-metro expansion sales office (San Antonio, Dallas, Austin, El Paso, Fort Worth, Galveston, Waco); Floor 3 hosts the executive suite and boardroom. Critically, the site includes 2.84 acres of platted, contiguous expansion land (Tract A) zoned to support a future build-to-suit Distribution Center / Medical Device Packaging Center — allowing Sterile Mate to scale B2B logistics without acquiring additional real estate. An additional 2.23-acre tract (Tract B) preserves further pad-site monetization or fleet-parking optionality. This dual-use land bank is a strategic ROI multiplier for the SBA lender — every dollar of expansion CapEx compounds on an already-collateralized asset.',
    locationHighlights: [
      '16651 Ronan Rd, Houston, TX 77060 — Greenspoint corporate corridor',
      '22,750 SF Class-A 3-story brick-clad executive HQ',
      '5.08 acres total site — 2.84 + 2.23 acres of surplus / expansion land (Tracts A + B)',
      '88 available on-site parking spaces',
      '2.84-acre expansion pad reserved for future Distribution / Medical Device Packaging Center',
      '200K+ daily vehicles — Beltway 8 + I-45 intersection visibility',
      '1.5 miles from the ExxonMobil headquarters campus',
      '8 minutes to George Bush Intercontinental Airport (IAH) — direct DME shipping access',
      'Top-floor private executive suite + adjacent boardroom',
      'Floor 2 (12,640 SF): 3,500 SF short-stock warehouse + Texas-metro sales office',
      'Formal BWS + MEP + RAS renovation program (~$160K) for 2nd-Level executive layout',
      '100% owner-occupied across all three floors — no third-party tenants',
    ],
    useOfFunds: [
      ['Building & Land Acquisition', '$3,500,000', '78.0%', '22,750 SF Class-A 3-story HQ on 5.08 acres — incl. 2.84-acre future Distribution / Med-Device Packaging pad'],
      ['Renovation & Capital Improvements', '$160,000', '3.6%', 'BWS architect + MEP + RAS: 2nd-Level estimate & layout ($102K), Schematic Design ($6,875), Design Development ($9,750), Construction Documents ($13,875), MEP Consultant ($25K), MEP Coordination ($2,500), RAS Fees & Construction Visitation'],
      ['Statewide B2B Marketing Launch', '$180,000', '4.0%', 'Texas-wide digital + medical-expo sponsorship + trade-show campaign'],
      ['Initial Consumables Inventory', '$150,000', '3.3%', '1× 4-mil glove container ($58,237) + 1× 3.5-mil glove container ($58,403) + 3,000 microfiber boxes (Blue/White/Black @ $11.24 = $33,720)'],
      ['Operating Capital & Working Reserves', '$500,000', '11.1%', '12-month operating reserve — structured as a revolving LOC'],
    ],
    equityNote:
      'Equity Injection: Approximately $384,000 (10% of the $3.84M SBA-financed portion — acquisition + renovation + marketing). The $500K operating capital is structured as a SEPARATE revolving Line of Credit, drawn as needed and paid back from operating cash flow — preserving the SBA 504 facility for the real estate + renovation + marketing deployment. Plan C is owner-occupied with seven diversified operating revenue streams, anchored by our flagship AskIsaac-automated Backorder Sourcing engine (Sterile Mate\u2019s 6-year benchmark and door-opener into every Texas hospital system, scaling $1.2M Y1 to $3.2M Y3), supported by Water Purification & Healthcare Capital Projects, Medical Device Sales, Healthcare Construction, Sterile Mate Private-Label Exam Gloves (Intco), Private-Label Microfiber (Texas MF), and Specialized BioBurden Cleaning — no third-party rental dependency.',
    fileName: 'SterileMate_Investor_Package_PlanC_Ronan_2026.pdf',
  },
};

const c = {
  primary: '#003366', secondary: '#0056A8', accent: '#60A5FA',
  success: '#10B981', warning: '#F59E0B', danger: '#EF4444',
  dark: '#1E293B', light: '#F8FAFC', muted: '#64748B', border: '#E2E8F0',
};

const s = StyleSheet.create({
  page: { padding: 40, backgroundColor: '#FFFFFF', fontFamily: 'Helvetica' },
  coverPage: { padding: 0, backgroundColor: c.primary },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, paddingBottom: 8, borderBottomWidth: 2, borderBottomColor: c.primary },
  headerText: { fontSize: 9, color: c.muted },
  title: { fontSize: 22, fontWeight: 'bold', color: c.primary, marginBottom: 15 },
  subtitle: { fontSize: 13, color: c.muted, marginBottom: 12 },
  p: { fontSize: 10, lineHeight: 1.6, color: c.dark, marginBottom: 10, textAlign: 'justify' },
  bold: { fontWeight: 'bold', color: c.secondary },
  row: { flexDirection: 'row' },
  card: { padding: 12, backgroundColor: c.light, borderRadius: 6, borderWidth: 1, borderColor: c.border, marginBottom: 8 },
  cardTitle: { fontSize: 11, fontWeight: 'bold', color: c.primary, marginBottom: 4 },
  cardText: { fontSize: 9, color: c.muted, lineHeight: 1.4 },
  metric: { padding: 10, backgroundColor: c.light, borderRadius: 6, alignItems: 'center', borderWidth: 1, borderColor: c.border },
  metricVal: { fontSize: 16, fontWeight: 'bold', color: c.primary, marginBottom: 2 },
  metricLbl: { fontSize: 7, color: c.muted, textAlign: 'center' },
  tHead: { flexDirection: 'row', backgroundColor: c.primary, padding: 8 },
  tHeadCell: { flex: 1, fontSize: 9, fontWeight: 'bold', color: '#FFFFFF' },
  tRow: { flexDirection: 'row', padding: 8, borderBottomWidth: 1, borderBottomColor: c.border },
  tRowAlt: { backgroundColor: c.light },
  tCell: { flex: 1, fontSize: 9, color: c.dark },
  bullet: { flexDirection: 'row', marginBottom: 5 },
  bulletDot: { width: 12, fontSize: 9, color: c.success },
  bulletText: { flex: 1, fontSize: 9, color: c.dark, lineHeight: 1.4 },
  footer: { position: 'absolute', bottom: 20, left: 40, right: 40, flexDirection: 'row', justifyContent: 'space-between', borderTopWidth: 1, borderTopColor: c.border, paddingTop: 8 },
  footerText: { fontSize: 7, color: c.muted },
  quoteBox: { backgroundColor: '#FEF3C7', borderLeftWidth: 3, borderLeftColor: c.warning, padding: 12, marginVertical: 10 },
  greenBox: { backgroundColor: '#ECFDF5', borderLeftWidth: 3, borderLeftColor: c.success, padding: 12, marginVertical: 10 },
  tocItem: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 7, borderBottomWidth: 1, borderBottomColor: c.border },
  tocText: { fontSize: 11, color: c.dark },
  tocPage: { fontSize: 11, color: c.primary, fontWeight: 'bold' },
});

const Header = ({ title, planLabel }) => (
  <View style={s.header}>
    <Text style={s.headerText}>Sterile Mate LLC | {planLabel}</Text>
    <Text style={s.headerText}>{title}</Text>
  </View>
);

const Footer = () => (
  <View style={s.footer}>
    <Text style={s.footerText}>Confidential — Sterile Mate LLC 2026</Text>
    <Text style={s.footerText}>george.broughton@sterilemate.com</Text>
  </View>
);

const Bullet = ({ children }) => (
  <View style={s.bullet}>
    <Text style={s.bulletDot}>•</Text>
    <Text style={s.bulletText}>{children}</Text>
  </View>
);

const InvestorPackagePDF = ({ scenario = 'humble' }) => {
  const cfg = SCENARIOS[scenario] || SCENARIOS.ronan;
  const planLabel = cfg.coverTitle;
  return (
  <Document>
    {/* ==================== COVER PAGE ==================== */}
    <Page size="A4" style={s.coverPage}>
      <View style={{ flex: 1 }}>
        <Image src={cfg.coverImage} style={{ width: '100%', height: 320, objectFit: 'cover' }} />
        <View style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 320, backgroundColor: 'rgba(0,51,102,0.55)' }} />
        <View style={{ position: 'absolute', top: 20, left: 30 }}>
          <Image src={STERILE_MATE_LOGO_WHITE} style={{ width: 160, height: 60, objectFit: 'contain' }} />
        </View>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 }}>
          <Text style={{ fontSize: 36, fontWeight: 'bold', color: '#FFFFFF', textAlign: 'center', marginBottom: 8 }}>
            Sterile Mate LLC
          </Text>
          <Text style={{ fontSize: 16, color: c.accent, textAlign: 'center', marginBottom: 6 }}>
            {cfg.coverTitle}
          </Text>
          <View style={{ backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 8, padding: 16, marginTop: 16, width: '90%' }}>
            <Text style={{ fontSize: 13, color: '#FFFFFF', textAlign: 'center', marginBottom: 6 }}>
              {cfg.coverFunds}
            </Text>
            <Text style={{ fontSize: 11, color: c.accent, textAlign: 'center' }}>
              {cfg.coverAddress}
            </Text>
          </View>
          <Text style={{ fontSize: 11, color: '#94A3B8', textAlign: 'center', marginTop: 20 }}>
            Three Revenue Streams. One Roof.{'\n'}B2B Healthcare | The Supply Doctor Retail | DME Depot
          </Text>
        </View>
        <View style={{ position: 'absolute', bottom: 30, left: 0, right: 0, alignItems: 'center' }}>
          <Text style={{ fontSize: 9, color: '#94A3B8' }}>George Broughton III, Founder & CEO</Text>
          <Text style={{ fontSize: 9, color: '#94A3B8' }}>george.broughton@sterilemate.com | Confidential</Text>
        </View>
      </View>
    </Page>

    {/* ==================== TABLE OF CONTENTS ==================== */}
    <Page size="A4" style={s.page}>
      <Header title="Table of Contents" planLabel={planLabel} />
      <Text style={s.title}>Contents</Text>
      {[
        { t: '1. Executive Summary', p: '3' },
        { t: '2. Business Description', p: '4' },
        { t: '3. Three Revenue Streams', p: '5' },
        { t: '4. Operations Plan — Building & Phases', p: '6' },
        { t: '5. Market Analysis & Pipeline', p: '7' },
        { t: '6. Competitive Advantages', p: '8' },
        { t: '7. Management & Leadership', p: '9' },
        { t: '8. Financial Projections', p: '10-11' },
        { t: `9. Use of Funds (${cfg.totalLabel})`, p: '12' },
        { t: '10. SBA Loan Structure & DSCR', p: '13' },
        { t: '11. Risk Factors & Mitigations', p: '14' },
        { t: '12. FAQ', p: '15' },
        { t: '13. Contact Information', p: '16' },
      ].map((item, idx) => (
        <View key={idx} style={s.tocItem}>
          <Text style={s.tocText}>{item.t}</Text>
          <Text style={s.tocPage}>{item.p}</Text>
        </View>
      ))}
      <Footer />
    </Page>

    {/* ==================== 1. EXECUTIVE SUMMARY ==================== */}
    <Page size="A4" style={s.page}>
      <Header title="Executive Summary" planLabel={planLabel} />
      <Text style={s.title}>Executive Summary</Text>
      <Text style={s.p}>
        <Text style={s.bold}>Sterile Mate LLC</Text> is a 6-year-old healthcare solutions provider serving hospitals across Texas and the VA system nationwide. Founded during COVID-19 by George Broughton III, the company has grown from a living room startup to a trusted partner with <Text style={s.bold}>$3.9M+ cumulative revenue</Text> and active contracts with one of the largest public health systems in the United States.
      </Text>
      <Text style={s.p}>
        We are seeking an <Text style={s.bold}>SBA 7(a) loan of {cfg.totalLabel}</Text> to acquire {cfg.id === 'humble'
          ? 'a 10,766 SF commercial building at 20121 Highway 59 N, Humble, TX'
          : cfg.id === 'ronan'
            ? 'a 22,750 SF Class-A 3-story corporate HQ on 5.08 acres at 16651 Ronan Rd, Houston, TX'
            : 'the 87,282 SF Class-B "2SIXTY3" office campus at 263 N Sam Houston Pkwy E, Houston, TX'} — establishing Sterile Mate's first fully integrated headquarters combining B2B operations, a retail medical supply storefront (The Supply Doctor), and a future DME/Insurance depot under one roof{cfg.id === 'northsam' ? ', plus multi-tenant rental income on surplus floor area' : cfg.id === 'ronan' ? ', anchored by seven diversified operating revenue streams led by the AskIsaac-automated Backorder Sourcing flagship (Sterile Mate\u2019s 6-year benchmark)' : ''}.
      </Text>
      <Text style={s.p}>
        Our technology platform, <Text style={s.bold}>AskIsaac</Text>, powers all operations — from procurement and compliance tracking to project execution and client reporting. Combined with exclusive partnerships in water purification, janitorial manufacturing, and healthcare software, Sterile Mate is positioned to scale from <Text style={s.bold}>$2.85M (Year 1) to $6.25M (Year 3)</Text>.
      </Text>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 10, marginBottom: 15 }}>
        {[
          { v: '$3.9M+', l: 'Cumulative Revenue' }, { v: '$1.182M', l: '2026 Pipeline' },
          { v: '6 Years', l: 'In Healthcare' }, { v: cfg.totalLabel, l: 'SBA Request' },
        ].map((m, i) => (
          <View key={i} style={{ ...s.metric, width: '23%' }}>
            <Text style={s.metricVal}>{m.v}</Text>
            <Text style={s.metricLbl}>{m.l}</Text>
          </View>
        ))}
      </View>
      <View style={s.greenBox}>
        <Text style={{ fontSize: 10, lineHeight: 1.5, color: c.dark }}>
          <Text style={{ fontWeight: 'bold' }}>Key Highlights:</Text> Already profitable company with existing revenue. Year 1 customers come from existing relationships — no cold outreach required. DSCR exceeds SBA minimums by Year 1 ({cfg.dscr.y1}). Four strategic partnerships generating diversified revenue streams.
        </Text>
      </View>
      <Footer />
    </Page>

    {/* ==================== 2. BUSINESS DESCRIPTION ==================== */}
    <Page size="A4" style={s.page}>
      <Header title="Business Description" planLabel={planLabel} />
      <Text style={s.title}>Business Description</Text>
      <Text style={s.subtitle}>Sterile Mate LLC — Founded 2020 | HUB Certified | Licensed Medical Device Supplier</Text>
      <Text style={s.p}>
        Sterile Mate LLC is a Texas-based healthcare solutions provider specializing in water purification systems, environmental services (EVS), janitorial manufacturing, and healthcare technology. The company serves hospitals, VA medical centers, and healthcare systems across Texas and nationwide.
      </Text>
      <Text style={{ ...s.subtitle, marginTop: 10 }}>Company Journey</Text>
      <View style={{ borderWidth: 1, borderColor: c.border, borderRadius: 4, marginBottom: 12 }}>
        <View style={s.tHead}>
          <Text style={s.tHeadCell}>Year</Text><Text style={s.tHeadCell}>Revenue</Text><Text style={{ ...s.tHeadCell, flex: 2 }}>Milestone</Text>
        </View>
        {[
          ['2020', '$0', 'Founded during COVID-19 crisis — frontline hospital supply'],
          ['2021', '$800K', 'Rapid growth — backorder sourcing for Texas Medical Center'],
          ['2022', '$700K', 'Launched healthcare water purification division'],
          ['2023', '$600K', 'Physical expansion — distribution center, VA contracts'],
          ['2024', '$1.1M', 'Major health system projects, AskIsaac development begins'],
          ['2025', '$1.5M+', 'AskIsaac platform launch — automates the 6-year backorder RFQ business (Sterile Mate\u2019s door-opener into every Texas hospital system) for regional/statewide scale'],
        ].map(([yr, rev, milestone], i) => (
          <View key={i} style={{ ...s.tRow, ...(i % 2 ? s.tRowAlt : {}) }}>
            <Text style={{ ...s.tCell, fontWeight: 'bold' }}>{yr}</Text>
            <Text style={{ ...s.tCell, color: c.success, fontWeight: 'bold' }}>{rev}</Text>
            <Text style={{ ...s.tCell, flex: 2 }}>{milestone}</Text>
          </View>
        ))}
      </View>
      <Text style={{ ...s.subtitle, marginTop: 8 }}>Strategic Partnerships</Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        {(cfg.id === 'ronan' ? [
          { n: 'Intco Medical', f: 'Private-Label 3.5-mil + 4.0-mil Nitrile Exam Gloves' },
          { n: 'Filmop USA', f: 'Janitorial Hardware & EVS Manufacturing' },
          { n: 'Empire Bio Diagnostics', f: 'ATP Testing & Borescopes (BioBurden Cleaning)' },
          { n: 'AskIsaac Platform', f: 'Automated Backorder RFQ engine — 6-yr Sterile Mate benchmark now digitized' },
        ] : [
          { n: 'Bleu Flux Beverage Co', f: 'Healthcare Water Purification & Bottled Water' },
          { n: 'Filmop USA', f: 'Janitorial Hardware & EVS Manufacturing' },
          { n: 'Empire Bio Diagnostics', f: 'ATP Testing & Borescopes' },
          { n: 'AskIsaac Platform', f: 'Automated Backorder RFQ engine' },
        ]).map((p, i) => (
          <View key={i} style={{ ...s.card, width: '48%' }}>
            <Text style={s.cardTitle}>{p.n}</Text>
            <Text style={s.cardText}>{p.f}</Text>
          </View>
        ))}
      </View>
      <Footer />
    </Page>

    {/* ==================== 3. REVENUE STREAMS ==================== */}
    <Page size="A4" style={s.page}>
      <Header title="Revenue Streams" planLabel={planLabel} />
      {cfg.id === 'ronan' ? (
        <>
          <Text style={s.title}>Seven Diversified Revenue Streams — Plan C</Text>
          <Text style={s.p}>Owner-occupied acquisition of 16651 Ronan Rd enables seven complementary revenue engines from a single Class-A HQ. Backorder Sourcing — Sterile Mate\u2019s 6-year benchmark and door-opener into every Texas hospital system — is now fully automated by the AskIsaac RFQ engine and stands as the flagship growth line, scaling from $1.2M Y1 to $3.2M Y3 as we extend regionally and statewide.</Text>
          <View style={{ flexDirection: 'row', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
            {[
              { t: 'Backorder Sourcing (AskIsaac \u00b7 flagship)', y1: '$1.2M', c: c.warning, note: '6-yr benchmark, now automated. Primary revenue engine scaling regionally + statewide.' },
              { t: 'Private-Label Exam Gloves (direct-import)', y1: '$749K', c: c.success, note: '8 containers \u00b7 9,040 cartons \u00b7 3.5-mil + 4.0-mil nitrile \u00b7 200 pcs/box.' },
              { t: 'Water Purification & Healthcare Capital Projects', y1: '$1.1M', c: c.primary, note: 'Facility-wide water systems + healthcare cap-ex.' },
              { t: 'Medical Device Sales', y1: '$800K', c: c.success, note: 'B2B distribution to hospitals & surgery centers.' },
              { t: 'Healthcare Construction / GC', y1: '$400K', c: c.secondary, note: 'OR / ED / cath-lab renovations, MEP coordination.' },
              { t: 'Specialized BioBurden Cleaning', y1: '$200K', c: c.primary, note: '5 hospitals \u00b7 OR beds, stretchers, ICU, OR tables & poles.' },
              { t: 'Private-Label Microfiber (Texas MF)', y1: '$92.5K', c: c.primary, note: '3,000 branded 50-count pop-up boxes \u00b7 Blue \u00b7 White \u00b7 Black.' },
            ].map((r, i) => (
              <View key={i} style={{ ...s.card, width: '48%', borderTopWidth: 3, borderTopColor: r.c }}>
                <Text style={{ ...s.cardTitle, fontSize: 11 }}>{r.t}</Text>
                <Text style={{ fontSize: 9, color: c.success, fontWeight: 'bold', marginBottom: 3 }}>Y1 Gross · {r.y1}</Text>
                <Text style={s.cardText}>{r.note}</Text>
              </View>
            ))}
          </View>
          <Text style={{ ...s.subtitle, marginTop: 6 }}>Revenue Projections by Stream ($)</Text>
          <View style={{ borderWidth: 1, borderColor: c.border, borderRadius: 4 }}>
            <View style={s.tHead}>
              <Text style={{ ...s.tHeadCell, flex: 2 }}>Revenue Stream</Text>
              <Text style={s.tHeadCell}>Year 1</Text><Text style={s.tHeadCell}>Year 2</Text><Text style={s.tHeadCell}>Year 3</Text>
            </View>
            {[
              ['Backorder Sourcing (AskIsaac-automated \u00b7 6-yr benchmark)', '$1,200,000', '$2,100,000', '$3,200,000'],
              ['Water Purification & Healthcare Capital Projects', '$1,100,000', '$1,400,000', '$1,800,000'],
              ['Medical Device Sales', '$800,000', '$1,100,000', '$1,500,000'],
              ['Healthcare Construction (GC / Renovation)', '$400,000', '$900,000', '$1,500,000'],
              ['Consumables \u00b7 Private-Label Gloves (Intco)', '$749,360', '$1,544,776', '$2,786,736'],
              ['Consumables \u00b7 Private-Label Microfiber (Texas MF)', '$92,500', '$191,000', '$354,600'],
              ['Specialized BioBurden Cleaning', '$200,000', '$350,000', '$500,000'],
              ['Total Revenue', '$4,541,860', '$7,585,776', '$11,641,336'],
            ].map(([stream, y1, y2, y3], i, arr) => {
              const isTotal = i === arr.length - 1;
              return (
                <View key={i} style={{ ...s.tRow, ...(isTotal ? { backgroundColor: '#ECFDF5' } : i % 2 ? s.tRowAlt : {}) }}>
                  <Text style={{ ...s.tCell, flex: 2, fontWeight: isTotal ? 'bold' : 'normal' }}>{stream}</Text>
                  <Text style={{ ...s.tCell, fontWeight: isTotal ? 'bold' : 'normal', color: isTotal ? c.success : c.dark }}>{y1}</Text>
                  <Text style={{ ...s.tCell, fontWeight: isTotal ? 'bold' : 'normal', color: isTotal ? c.success : c.dark }}>{y2}</Text>
                  <Text style={{ ...s.tCell, fontWeight: isTotal ? 'bold' : 'normal', color: isTotal ? c.success : c.dark }}>{y3}</Text>
                </View>
              );
            })}
          </View>
        </>
      ) : (
        <>
          <Text style={s.title}>Three Revenue Streams, One Roof</Text>
          <Text style={s.p}>The building acquisition enables three distinct, complementary revenue engines operating from a single headquarters — maximizing asset utilization and creating multiple paths to revenue growth.</Text>
          <View style={{ flexDirection: 'row', gap: 8, marginBottom: 12 }}>
            <View style={{ ...s.card, flex: 1, borderTopWidth: 3, borderTopColor: c.primary }}>
              <Text style={{ ...s.cardTitle, fontSize: 12 }}>Stream 1: B2B Healthcare</Text>
              <Text style={{ fontSize: 8, color: c.success, fontWeight: 'bold', marginBottom: 4 }}>Day 1 Revenue — Existing Contracts</Text>
              <Text style={s.cardText}>Water purification systems, janitorial/EVS products, and the AskIsaac-automated Backorder Sourcing engine (6-yr Sterile Mate benchmark). Active Harris Health pipeline: $1.182M.</Text>
            </View>
            <View style={{ ...s.card, flex: 1, borderTopWidth: 3, borderTopColor: c.success }}>
              <Text style={{ ...s.cardTitle, fontSize: 12 }}>Stream 2: Retail (The Supply Doctor)</Text>
              <Text style={{ fontSize: 8, color: c.warning, fontWeight: 'bold', marginBottom: 4 }}>Phase I Launch — Mid Year 1</Text>
              <Text style={s.cardText}>Retail medical supply storefront (DBA "The Supply Doctor"). {cfg.id === 'humble' ? '168,000 daily vehicles on US-59 frontage.' : '200,000+ daily vehicles on Beltway 8 frontage.'} PPE, first aid, mobility aids, home health products.</Text>
            </View>
            <View style={{ ...s.card, flex: 1, borderTopWidth: 3, borderTopColor: c.warning }}>
              <Text style={{ ...s.cardTitle, fontSize: 12 }}>Stream 3: DME Depot</Text>
              <Text style={{ fontSize: 8, color: c.secondary, fontWeight: 'bold', marginBottom: 4 }}>Phase II — Year 2</Text>
              <Text style={s.cardText}>Durable Medical Equipment distribution (Vive Health, MHC Medical). Medicare/private insurance billing. High-margin recurring revenue from DME products.</Text>
            </View>
          </View>

          <Text style={{ ...s.subtitle, marginTop: 8 }}>Revenue Projections by Stream</Text>
          <View style={{ borderWidth: 1, borderColor: c.border, borderRadius: 4 }}>
            <View style={s.tHead}>
              <Text style={{ ...s.tHeadCell, flex: 2 }}>Revenue Stream</Text>
              <Text style={s.tHeadCell}>Year 1</Text><Text style={s.tHeadCell}>Year 2</Text><Text style={s.tHeadCell}>Year 3</Text>
            </View>
            {[
              ['Water Purification Projects', '$828K', '$1,160K', '$1,812K'],
              ['Janitorial / EVS', '$427K', '$600K', '$938K'],
              ['Backorder Sourcing (AskIsaac)', '$1,200K', '$1,520K', '$2,000K'],
              ['Retail — The Supply Doctor', '—', '$200K', '$500K'],
              ['DME / Insurance Billing', '—', '$200K', '$500K'],
            ].map(([stream, y1, y2, y3], i) => (
              <View key={i} style={{ ...s.tRow, ...(i % 2 ? s.tRowAlt : {}) }}>
                <Text style={{ ...s.tCell, flex: 2 }}>{stream}</Text>
                <Text style={s.tCell}>{y1}</Text><Text style={s.tCell}>{y2}</Text>
                <Text style={{ ...s.tCell, fontWeight: 'bold' }}>{y3}</Text>
              </View>
            ))}
            <View style={{ ...s.tRow, backgroundColor: '#ECFDF5' }}>
              <Text style={{ ...s.tCell, flex: 2, fontWeight: 'bold', color: c.primary }}>TOTAL REVENUE</Text>
              <Text style={{ ...s.tCell, fontWeight: 'bold' }}>$2,850K</Text>
              <Text style={{ ...s.tCell, fontWeight: 'bold' }}>$4,000K</Text>
              <Text style={{ ...s.tCell, fontWeight: 'bold', color: c.success }}>$6,250K</Text>
            </View>
          </View>
        </>
      )}
      {cfg.id === 'northsam' && (
        <View style={s.greenBox}>
          <Text style={{ fontSize: 9, color: c.dark, lineHeight: 1.4 }}>
            <Text style={{ fontWeight: 'bold' }}>Plan B — Ancillary Rental Income:</Text> The 87,282 SF "2SIXTY3" campus exceeds Sterile Mate&apos;s direct occupancy needs. Approximately 50,000 SF of surplus space is leased multi-tenant at market rates ($12–$15/SF NNN), generating $300K–$450K of additional annual income that is layered on top of operating profit and applied directly to debt service.
          </Text>
        </View>
      )}
      {cfg.id === 'ronan' && (
        <View style={s.greenBox}>
          <Text style={{ fontSize: 9, color: c.dark, lineHeight: 1.4 }}>
            <Text style={{ fontWeight: 'bold' }}>Plan C — Owner-Occupied, Seven Revenue Streams:</Text> Sterile Mate occupies all three floors of 16651 Ronan Rd — no third-party tenants. The property functions purely as an operating headquarters supporting seven diversified revenue engines, anchored by our flagship AskIsaac-automated Backorder Sourcing line (Sterile Mate\u2019s 6-year benchmark and door-opener into every Texas hospital system, scaling $1.2M Y1 to $3.2M Y3). The Sterile Mate Private-Label Exam Glove line (direct import from Haiphong, Vietnam — 3.5-mil + 4.0-mil nitrile, 200 pcs/box, 2,000 gloves per master case) anchors a scale-up from $749K Y1 (8 containers) to $2.79M Y3 (28 containers), and the Specialized BioBurden Cleaning Service adds a $200K Y1 recurring stream across 5 hospitals. Total Y1 revenue $4.54M scales to $11.64M by Y3; SBA 504 Y2 DSCR lands at 6.71x on operating cash alone.
          </Text>
        </View>
      )}
      <Footer />
    </Page>

    {/* ==================== 4. OPERATIONS PLAN ==================== */}
    <Page size="A4" style={s.page}>
      <Header title="Operations Plan" planLabel={planLabel} />
      <Text style={s.title}>{cfg.operationsHeadline}</Text>
      <Text style={s.subtitle}>{cfg.operationsSub}</Text>
      <Text style={s.p}>{cfg.operationsLead}</Text>
      <View style={{ flexDirection: 'row', gap: 8, marginBottom: 12 }}>
        <View style={{ ...s.card, flex: 1, borderLeftWidth: 3, borderLeftColor: c.success }}>
          <Text style={{ ...s.cardTitle, color: c.success }}>Phase I (Year 1)</Text>
          <Text style={{ ...s.cardTitle, fontSize: 10 }}>B2B HQ + The Supply Doctor</Text>
          <Bullet>Showroom: Water purification & EVS product displays</Bullet>
          <Bullet>Training Center: Customer & staff certification space</Bullet>
          <Bullet>Warehouse: Inventory staging & logistics hub</Bullet>
          <Bullet>Retail: The Supply Doctor storefront (DBA)</Bullet>
          <Bullet>$10K/mo marketing engine activated (billboards, digital)</Bullet>
          {cfg.id === 'northsam' && <Bullet>Multi-tenant lease-up of surplus floor area</Bullet>}
          {cfg.id === 'ronan' && <Bullet>Floor 2 (12,640 SF): 3,500 SF short-stock warehouse + Texas-metro sales office (SA · Dallas · Austin · El Paso · FW · Galveston · Waco)</Bullet>}
        </View>
        <View style={{ ...s.card, flex: 1, borderLeftWidth: 3, borderLeftColor: c.secondary }}>
          <Text style={{ ...s.cardTitle, color: c.secondary }}>Phase II (Year 2+)</Text>
          <Text style={{ ...s.cardTitle, fontSize: 10 }}>DME Depot + Insurance</Text>
          <Bullet>Durable Medical Equipment showroom & distribution</Bullet>
          <Bullet>Medicare/private insurance billing capability</Bullet>
          <Bullet>Vive Health & MHC Medical product lines</Bullet>
          <Bullet>3 revenue streams fully operational</Bullet>
          <Bullet>DSCR {cfg.dscr.y2}–{cfg.dscr.y3} (exceeds SBA minimums)</Bullet>
        </View>
      </View>
      <Text style={{ ...s.subtitle, marginTop: 8 }}>Technology Platform: AskIsaac</Text>
      <Text style={s.p}>
        AskIsaac is our proprietary AI-powered operations platform. It powers procurement, compliance tracking, project management, and real-time communication across all three revenue streams. Key capabilities include Telegram-first UX (nurses text from the floor), AI-powered routing, broadcast vendor matching, and automated compliance frameworks.
      </Text>
      <View style={{ flexDirection: 'row', gap: 8 }}>
        {[
          { t: 'Telegram-First UX', d: 'No app download required. Staff text requests from the floor.' },
          { t: 'AI Routing', d: 'Natural language processing classifies intent and routes automatically.' },
          { t: 'Pre-Vetted Network', d: 'Suppliers credentialed and ready before you need them.' },
          { t: 'Compliance Engine', d: 'AAMI ST108, audit trails, 3-quote rules — handled automatically.' },
        ].map((item, i) => (
          <View key={i} style={{ ...s.card, width: '24%' }}>
            <Text style={{ ...s.cardTitle, fontSize: 9 }}>{item.t}</Text>
            <Text style={{ fontSize: 8, color: c.muted, lineHeight: 1.3 }}>{item.d}</Text>
          </View>
        ))}
      </View>
      <Footer />
    </Page>

    {/* ==================== 4b. PROPERTY DETAIL — PLAN B ONLY ==================== */}
    {cfg.id === 'northsam' && (
      <Page size="A4" style={s.page}>
        <Header title="Property Detail — 2SIXTY3" planLabel={planLabel} />
        <Text style={s.title}>2SIXTY3 — Property Detail</Text>
        <Text style={s.subtitle}>263 N Sam Houston Pkwy E, Houston, TX 77060</Text>
        <Image src={cfg.coverImage} style={{ width: '100%', height: 200, objectFit: 'cover', borderRadius: 6, marginBottom: 12 }} />
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {[
            ['Building Size', '87,282 SF'],
            ['Stories', '3'],
            ['Year Built / Renovated', '1978 / 2023'],
            ['Building Class', 'B (Institutional)'],
            ['Typical Floor Plate', '29,094 SF'],
            ['Unfinished Ceiling Height', '11\u2032'],
            ['Parking', '213 Surface + Covered Garage'],
            ['Daily Traffic', '180,000 vehicles'],
            ['Submarket', 'North Houston District'],
            ['Access', 'I-45 \u00b7 Beltway 8 \u00b7 Hardy Toll Rd'],
          ].map(([k, v], i) => (
            <View key={i} style={{ ...s.metric, width: '19%', padding: 6 }}>
              <Text style={{ ...s.metricLbl, marginBottom: 2 }}>{k}</Text>
              <Text style={{ fontSize: 9, fontWeight: 'bold', color: c.primary, textAlign: 'center' }}>{v}</Text>
            </View>
          ))}
        </View>
        <Text style={{ ...s.subtitle, marginTop: 6 }}>Amenities & Highlights</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
          {[
            'Secured campus environment',
            'On-site security & 24-hour access',
            'Controlled access & security system',
            'Property manager on site',
            'Open & closed office concepts',
            'Move-in ready "plug & play" with furniture',
            'Single-tenant or multi-tenant configurations',
            'Future cafe space',
            'Walkable to 10+ dining options',
            'Prominent building signage opportunity',
            'Covered garage + secured surface lot',
            'Direct access to George Bush Intercontinental Airport (12 min)',
          ].map((a, i) => (
            <View key={i} style={{ width: '50%', flexDirection: 'row', marginBottom: 4 }}>
              <Text style={{ width: 12, fontSize: 9, color: c.success }}>•</Text>
              <Text style={{ flex: 1, fontSize: 9, color: c.dark }}>{a}</Text>
            </View>
          ))}
        </View>
        <View style={s.greenBox}>
          <Text style={{ fontSize: 9, color: c.dark, lineHeight: 1.4 }}>
            <Text style={{ fontWeight: 'bold' }}>Multi-Tenant Income Strategy:</Text> Sterile Mate occupies one floor (~29,000 SF) as the integrated HQ, AskIsaac engineering suite, training center, showroom, and the Supply Doctor retail front. The remaining ~58,000 SF is leased to credit-quality multi-tenants at market rates, providing $300K–$450K of annual rental income that is applied directly to SBA debt service and strengthens DSCR across all three years of projections.
          </Text>
        </View>
        <Footer />
      </Page>
    )}

    {/* ==================== 4c. PROPERTY DETAIL — PLAN C ONLY (16651 Ronan Rd) ==================== */}
    {cfg.id === 'ronan' && (
      <Page size="A4" style={s.page}>
        <Header title="Property Detail — 16651 Ronan Rd" planLabel={planLabel} />
        <Text style={s.title}>16651 Ronan Rd — Property Detail</Text>
        <Text style={s.subtitle}>16651 Ronan Rd, Houston, TX 77060 — Greenspoint Corporate Corridor</Text>
        <Image src={cfg.coverImage} style={{ width: '100%', height: 200, objectFit: 'cover', borderRadius: 6, marginBottom: 12 }} />
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {[
            ['Building Size', '22,750 SF'],
            ['Stories', '3'],
            ['Building Class', 'A (Corporate HQ)'],
            ['Lot Size', '5.08 Acres'],
            ['Floor 1', '10,438 SF — Sterile Mate'],
            ['Floor 2', '12,640 SF — SM Warehouse + Sales'],
            ['Floor 3', 'Executive Suite + Boardroom'],
            ['Expansion Pad', '2.84 Ac — Distribution'],
            ['Parking', '88 Available Spaces'],
            ['Access', 'Beltway 8 \u00b7 I-45'],
          ].map(([k, v], i) => (
            <View key={i} style={{ ...s.metric, width: '19%', padding: 6 }}>
              <Text style={{ ...s.metricLbl, marginBottom: 2 }}>{k}</Text>
              <Text style={{ fontSize: 9, fontWeight: 'bold', color: c.primary, textAlign: 'center' }}>{v}</Text>
            </View>
          ))}
        </View>
        <Text style={{ ...s.subtitle, marginTop: 6 }}>Amenities & Highlights</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
          {[
            'Class-A finish — brick exterior, executive lobby with fireplace',
            'Top-floor private executive suite + adjacent boardroom',
            'Multiple conference rooms (1st, 2nd, 3rd floor)',
            'Courtyard & landscaped patios with seating',
            'Two stairwells + elevator — ADA accessible',
            'Workout / wellness room on 2nd floor',
            'Lunch room + employee lounge',
            'Server room + IT room + A/V closet',
            'Existing single-tenant build-out — minimal demo required',
            '2.84 acres reserved for future Distribution Center / Medical Device Packaging Center',
            '88 available on-site parking spaces',
            '2.23 acres of additional flex / fleet-parking land (Tract B)',
            'Direct Beltway 8 frontage — ExxonMobil campus 1.5 mi north',
            '8 minutes to George Bush Intercontinental Airport (IAH)',
          ].map((a, i) => (
            <View key={i} style={{ width: '50%', flexDirection: 'row', marginBottom: 4 }}>
              <Text style={{ width: 12, fontSize: 9, color: c.success }}>•</Text>
              <Text style={{ flex: 1, fontSize: 9, color: c.dark }}>{a}</Text>
            </View>
          ))}
        </View>
        <View style={s.greenBox}>
          <Text style={{ fontSize: 9, color: c.dark, lineHeight: 1.4 }}>
            <Text style={{ fontWeight: 'bold' }}>Owner-Occupied \u00b7 Seven Diversified Revenue Streams:</Text> Sterile Mate uses all three floors — no third-party tenants. Floor 1 (10,438 SF): HQ, showroom, training center. Floor 2 (12,640 SF): 3,500 SF short-stock warehouse plus the Texas-metro expansion sales office (San Antonio, Dallas, Austin, El Paso, Fort Worth, Galveston, Waco). Floor 3: executive suite &amp; boardroom. Year-1 revenue is anchored by our flagship <Text style={{ fontWeight: 'bold' }}>Backorder Sourcing engine ($1.2M Y1 → $3.2M Y3)</Text> — Sterile Mate\u2019s 6-year benchmark and door-opener into every Texas hospital system, now fully automated by AskIsaac for regional and statewide scale — supported by six additional streams: Water Purification &amp; Healthcare Capital Projects ($1.1M), Medical Device Sales ($800K), Healthcare Construction / GC ($400K), Sterile Mate Private-Label Exam Gloves (direct-import from Haiphong Vietnam, 200 pcs/box, 8 containers scaling to 28 by Y3 — $749K Y1 \u00b7 $1.54M Y2 \u00b7 $2.79M Y3, 37.8\u201341.4% gross margins), Private-Label Microfiber (Texas MF, $92.5K Y1), and Specialized BioBurden Cleaning ($200K — 5 hospitals \u00b7 OR beds, stretchers, ICU beds, OR tables &amp; poles). Year-1 total <Text style={{ fontWeight: 'bold' }}>$4.54M</Text> scaling to <Text style={{ fontWeight: 'bold' }}>$11.64M</Text> by Y3; Year-2 DSCR 6.71x on the SBA 504 stack.
          </Text>
        </View>
        <View style={{ backgroundColor: '#EFF6FF', borderLeftWidth: 3, borderLeftColor: c.primary, padding: 12, marginVertical: 10 }}>
          <Text style={{ fontSize: 11, fontWeight: 'bold', color: c.primary, marginBottom: 4 }}>
            Growth Potential — 2.84 Acres Future Distribution Center / Medical Device Packaging
          </Text>
          <Text style={{ fontSize: 9, color: c.dark, lineHeight: 1.4 }}>
            The 16651 Ronan Rd site includes <Text style={{ fontWeight: 'bold' }}>2.84 acres of platted, contiguous expansion land (Tract A)</Text> directly adjacent to the existing Class-A HQ. As Sterile Mate scales its <Text style={{ fontWeight: 'bold' }}>B2B healthcare logistics and Online DME retail fulfillment</Text> for hospitals, surgery centers, and direct-to-patient Medicare/Medicaid clients, this tract is reserved for a <Text style={{ fontWeight: 'bold' }}>future build-to-suit Distribution Center and Medical Device Packaging Center</Text>. This means the company will never have to identify, acquire, or relocate to a new piece of real estate to scale — every dollar of future expansion CapEx is invested back into the already-collateralized SBA asset, <Text style={{ fontWeight: 'bold' }}>compounding the lender's loan-to-value position and lifting long-term ROI</Text>. Combined with the additional 2.23-acre Tract B (reserved for fleet parking or pad-site monetization), the property functions as both today's headquarters and tomorrow's logistics campus on a single, lender-friendly title.
          </Text>
        </View>
        <Footer />
      </Page>
    )}

    {/* ==================== 5. MARKET ANALYSIS & PIPELINE ==================== */}
    <Page size="A4" style={s.page}>
      <Header title="Market Analysis" planLabel={planLabel} />
      <Text style={s.title}>Market Analysis & Pipeline</Text>

      {/* HEADLINE BULLETIN — Backorder Sourcing = Everyday Healthcare Crisis */}
      <View style={{ borderWidth: 2, borderColor: c.warning, backgroundColor: '#FFF7ED', borderRadius: 6, padding: 12, marginBottom: 12 }}>
        <Text style={{ fontSize: 8, color: c.warning, fontWeight: 'bold', letterSpacing: 1, marginBottom: 4 }}>HEADLINE MARKET OPPORTUNITY \u00b7 LIVE SIGNAL</Text>
        <Text style={{ fontSize: 14, fontWeight: 'bold', color: c.dark, marginBottom: 4 }}>&ldquo;Backorder&rdquo; is Healthcare&rsquo;s All-Hands-on-Deck Signal — and It Happens Every Day.</Text>
        <Text style={{ fontSize: 9, color: c.dark, lineHeight: 1.4, marginBottom: 6 }}>
          Backorders paralyze hospitals every single day. Severe-disaster surges (hurricanes, wildfires, pandemics, supply-chain shocks) flood us with SOS requests from multiple regions of the country at the same time. Sterile Mate has been the answer to that signal for <Text style={{ fontWeight: 'bold' }}>six years</Text> — backorder sourcing is our benchmark and door-opener into every Texas hospital system. We are already tracking to <Text style={{ fontWeight: 'bold', color: c.warning }}>top the backorder-sourcing volume last seen during COVID-19</Text>.
        </Text>
        <Text style={{ fontSize: 9, color: c.dark, lineHeight: 1.4, marginBottom: 6 }}>
          With <Text style={{ fontWeight: 'bold' }}>AskIsaac now automating the full RFQ workflow</Text> — instant multi-supplier broadcast, ETA tracking, hospital-alert engine — we can scale this business from a Texas footprint into a national procurement operation. Competitors (CIA Medical, Grayline Medical, Vitality Medical) have taken partial advantage of the category as catalog distributors, but <Text style={{ fontWeight: 'bold' }}>none combine a growing national supplier network across every region with automated RFQ dispatch and 6 years of embedded hospital relationships</Text>.
        </Text>
        <Text style={{ fontSize: 9, color: c.dark, lineHeight: 1.4, fontStyle: 'italic' }}>
          Today the CEO personally handles every backorder request in the pipeline — that is the ceiling. Deploy an inside-sales team plugged into AskIsaac and healthcare-outreach automation, and this single line item becomes a national procurement business. This is a gold mine hiding in plain sight.
        </Text>
      </View>

      {/* Competitor Landscape Table */}
      <Text style={{ ...s.subtitle, fontWeight: 'bold' }}>Competitor Landscape — Why They Can\u2019t Respond Like We Do</Text>
      <View style={{ borderWidth: 1, borderColor: c.border, borderRadius: 4, marginBottom: 12 }}>
        <View style={s.tHead}>
          <Text style={{ ...s.tHeadCell, flex: 1.6 }}>Player</Text>
          <Text style={{ ...s.tHeadCell, flex: 2.4 }}>Model</Text>
          <Text style={s.tHeadCell}>Regional Speed</Text>
          <Text style={s.tHeadCell}>National Response</Text>
          <Text style={s.tHeadCell}>Automated RFQ</Text>
        </View>
        {[
          ['Sterile Mate + AskIsaac', 'Automated national procurement w/ 6-yr hospital relationships', 'Minutes', 'Every Region', 'Full RFQ Engine', true],
          ['CIA Medical', 'Catalog-first distributor — online product search', 'Hours\u2013Days', 'Limited', 'Manual', false],
          ['Grayline Medical', 'Discount medical & DME reseller', 'Hours\u2013Days', 'Limited', 'Manual', false],
          ['Vitality Medical', 'Consumer/DME e-commerce with B2B add-on', 'Slow', 'Retail-first', 'None', false],
        ].map(([name, model, spd, natl, auto, hl], i) => (
          <View key={i} style={{ ...s.tRow, ...(hl ? { backgroundColor: '#ECFDF5' } : i % 2 ? s.tRowAlt : {}) }}>
            <Text style={{ ...s.tCell, flex: 1.6, fontWeight: hl ? 'bold' : 'normal', color: hl ? c.success : c.dark }}>{name}</Text>
            <Text style={{ ...s.tCell, flex: 2.4 }}>{model}</Text>
            <Text style={{ ...s.tCell, fontWeight: hl ? 'bold' : 'normal', color: hl ? c.success : c.dark }}>{spd}</Text>
            <Text style={{ ...s.tCell, fontWeight: hl ? 'bold' : 'normal', color: hl ? c.success : c.dark }}>{natl}</Text>
            <Text style={{ ...s.tCell, fontWeight: hl ? 'bold' : 'normal', color: hl ? c.success : c.dark }}>{auto}</Text>
          </View>
        ))}
      </View>

      <Text style={{ ...s.subtitle, fontWeight: 'bold' }}>Market Sizing (Combined Healthcare Infrastructure + DME/Mobility)</Text>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 12 }}>
        {[
          { v: '$47B', l: 'TAM', bg: '#EFF6FF', co: c.primary },
          { v: '$8.2B', l: 'SAM', bg: '#ECFDF5', co: c.success },
          { v: '$180M', l: 'SOM (Y5)', bg: '#FEF3C7', co: c.warning },
        ].map((m, i) => (
          <View key={i} style={{ width: '31%', padding: 10, backgroundColor: m.bg, borderRadius: 6, alignItems: 'center' }}>
            <Text style={{ fontSize: 8, color: m.co, marginBottom: 4 }}>
              {['Total Addressable', 'Serviceable Addressable', 'Serviceable Obtainable'][i]}
            </Text>
            <Text style={{ fontSize: 18, fontWeight: 'bold', color: m.co }}>{m.v}</Text>
          </View>
        ))}
      </View>
      <Text style={{ ...s.subtitle, fontWeight: 'bold' }}>Active Pipeline: $1.182M — Year 1 Confirmed</Text>
      <View style={{ borderWidth: 1, borderColor: c.border, borderRadius: 4, marginBottom: 12 }}>
        <View style={s.tHead}>
          <Text style={{ ...s.tHeadCell, flex: 2 }}>Project</Text>
          <Text style={s.tHeadCell}>Value</Text><Text style={s.tHeadCell}>Status</Text>
        </View>
        {[
          ['Harris Health — Water Softening Systems', '$791K', 'In Progress'],
          ['Harris Health — Dialysis Water Purification', '$391K', 'In Progress'],
          ['Automated Backorder Sourcing (AskIsaac)', 'Flagship', 'Scaling'],
        ].map(([p, v, st], i) => (
          <View key={i} style={{ ...s.tRow, ...(i % 2 ? s.tRowAlt : {}) }}>
            <Text style={{ ...s.tCell, flex: 2 }}>{p}</Text>
            <Text style={{ ...s.tCell, fontWeight: 'bold', color: c.success }}>{v}</Text>
            <Text style={{ ...s.tCell, color: c.secondary }}>{st}</Text>
          </View>
        ))}
      </View>
      <View style={s.greenBox}>
        <Text style={{ fontSize: 9, color: c.dark, lineHeight: 1.4 }}>
          <Text style={{ fontWeight: 'bold' }}>Sales Cycle: 90 days</Text> (vs. 180+ industry average). Year 1 customers come from existing Sterile Mate relationships in the Texas Medical Center — no cold outreach required. Backorder-sourcing volume is already tracking to top the last COVID-era peak.
        </Text>
      </View>
      <Footer />
    </Page>

    {/* ==================== 6. COMPETITIVE ADVANTAGES ==================== */}
    <Page size="A4" style={s.page}>
      <Header title="Competitive Advantages" planLabel={planLabel} />
      <Text style={s.title}>Why Competitors Cannot Replicate This</Text>
      <Text style={s.subtitle}>Software can be copied. Trust cannot.</Text>
      {[
        { t: '6 Years of Embedded Relationships', d: "We've been in the Texas Medical Center since 2020. A competitor needs 5+ years just to get the first meeting. These aren't leads — they're colleagues.", def: 'High' },
        { t: 'Automated Backorder Sourcing (6-yr benchmark)', d: 'Sterile Mate has been the go-to backorder-sourcing partner for the Texas Medical Center since 2020. AskIsaac now digitizes six years of supplier relationships, pricing history, and hospital-specific SKU maps into a fully automated RFQ engine. A competitor would need to rebuild both the software AND the six-year benchmark relationships to compete.', def: 'Very High' },
        { t: 'Hospital Trust Takes Years to Build', d: 'Vendor vetting, compliance audits, security reviews, board approvals — we have passed them all. A new entrant starts at zero.', def: 'Very High' },
        { t: 'Two-Sided Marketplace Network Effects', d: 'Every hospital added makes the platform more valuable to suppliers. Every supplier added makes it more valuable to hospitals.', def: 'Very High' },
        { t: 'HIPAA & Compliance Expertise', d: 'Zero-knowledge architecture, WORM audit logs, PHI detection. Built by people who understand what "audit-ready" means.', def: 'High' },
        { t: 'Founder-Market Fit', d: "George built this company on the front lines during COVID-19. Competitors can copy features; they can't copy authenticity.", def: 'Unmatched' },
      ].map((item, i) => (
        <View key={i} style={{ ...s.card, flexDirection: 'row', alignItems: 'flex-start', gap: 10 }}>
          <View style={{ flex: 1 }}>
            <Text style={{ ...s.cardTitle, fontSize: 10 }}>{item.t}</Text>
            <Text style={s.cardText}>{item.d}</Text>
          </View>
          <View style={{ backgroundColor: c.success + '20', borderRadius: 4, padding: 4 }}>
            <Text style={{ fontSize: 7, color: c.success, fontWeight: 'bold' }}>{item.def}</Text>
          </View>
        </View>
      ))}
      <Footer />
    </Page>

    {/* ==================== 7. MANAGEMENT ==================== */}
    <Page size="A4" style={s.page}>
      <Header title="Management" planLabel={planLabel} />
      <Text style={s.title}>Management & Leadership</Text>
      <View style={{ ...s.card, width: '100%', marginBottom: 15 }}>
        <Text style={{ fontSize: 16, fontWeight: 'bold', color: c.primary, marginBottom: 4 }}>George Broughton III</Text>
        <Text style={{ fontSize: 11, color: c.secondary, marginBottom: 10 }}>Founder & CEO, Sterile Mate LLC | Creator of AskIsaac.io</Text>
        <Text style={s.p}>
          George founded Sterile Mate LLC from his living room during COVID-19 as a frontline triage supplier for Houston hospitals. What began as an emergency response became a mission: build a company that healthcare facilities can depend on — not just in a crisis, but every day. From a startup to a HUB-certified government contractor with $3.9M+ in cumulative revenue, George has built Sterile Mate through relationships, tenacity, and deep personal commitment to healthcare.
        </Text>
        <Text style={s.p}>
          He also created AskIsaac.io, a proprietary AI-powered platform that now powers all of Sterile Mate's operations. He is leading the company's most ambitious move: acquiring {cfg.id === 'humble' ? 'a 10,766 SF commercial headquarters' : cfg.id === 'ronan' ? 'a 22,750 SF Class-A 3-story corporate HQ on 5.08 acres at 16651 Ronan Rd in Greenspoint' : 'the 87,282 SF "2SIXTY3" campus'} to house B2B operations, a retail storefront (The Supply Doctor), and a DME depot.
        </Text>
        <Text style={{ fontSize: 9, fontWeight: 'bold', color: c.dark }}>HUB Certified | Licensed Medical Device Supplier | Active Government Contracts</Text>
      </View>
      <View style={s.quoteBox}>
        <Text style={{ fontSize: 10, fontStyle: 'italic', color: c.dark, lineHeight: 1.5 }}>
          "I built this company because I saw hospitals struggling to get what they needed, when they needed it. Now we're building a headquarters to serve them even better — combining B2B operations, retail medical supply, and DME under one roof."
        </Text>
      </View>
      <View style={{ flexDirection: 'row', gap: 10, marginTop: 15 }}>
        {[{ v: '$3.9M+', l: 'Revenue Generated' }, { v: '$1.182M', l: '2026 Pipeline' }, { v: '6 Years', l: 'Healthcare Experience' }, { v: '5+', l: 'Health System Clients' }].map((m, i) => (
          <View key={i} style={{ ...s.metric, flex: 1 }}>
            <Text style={s.metricVal}>{m.v}</Text>
            <Text style={s.metricLbl}>{m.l}</Text>
          </View>
        ))}
      </View>
      <Footer />
    </Page>

    {/* ==================== 8. FINANCIAL PROJECTIONS (P1) ==================== */}
    <Page size="A4" style={s.page}>
      <Header title="Financial Projections" planLabel={planLabel} />
      <Text style={s.title}>3-Year Financial Projections</Text>
      <Text style={s.subtitle}>SBA Expansion Model — Profitable from Year 1</Text>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 15 }}>
        {[
          { l: 'Year 1 (2026)', r: '$2.85M', n: '+$194K Net', bg: '#EFF6FF', nc: c.warning },
          { l: 'Year 2 (2027)', r: '$4.0M', n: '+$480K Net', bg: '#ECFDF5', nc: c.success },
          { l: 'Year 3 (2028)', r: '$6.25M', n: '+$1.25M Net', bg: '#F0FDF4', nc: c.success },
        ].map((y, i) => (
          <View key={i} style={{ width: '31%', padding: 12, backgroundColor: y.bg, borderRadius: 6, alignItems: 'center' }}>
            <Text style={{ fontSize: 9, color: c.muted, marginBottom: 4 }}>{y.l}</Text>
            <Text style={{ fontSize: 18, fontWeight: 'bold', color: c.primary }}>{y.r}</Text>
            <Text style={{ fontSize: 10, fontWeight: 'bold', color: y.nc, marginTop: 4 }}>{y.n}</Text>
          </View>
        ))}
      </View>
      <Text style={s.subtitle}>Income Statement Summary</Text>
      <View style={{ borderWidth: 1, borderColor: c.border, borderRadius: 4, marginBottom: 12 }}>
        <View style={s.tHead}>
          <Text style={{ ...s.tHeadCell, flex: 2 }}>Metric</Text>
          <Text style={s.tHeadCell}>Year 1</Text><Text style={s.tHeadCell}>Year 2</Text><Text style={s.tHeadCell}>Year 3</Text>
        </View>
        {(cfg.id === 'ronan' ? [
          ['Total Revenue', '$3,641,860', '$6,035,776', '$9,291,336', false],
          ['Cost of Sales (gloves + microfiber + other)', '$1,022,428', '$1,694,921', '$2,634,069', false],
          ['Gross Profit', '$2,619,432', '$4,340,855', '$6,657,267', true],
          ['Operating Expenses (ex. debt service)', '$1,252,500', '$1,525,500', '$1,855,500', false],
          ['SBA 504 + Existing + LOC Debt Service', '$446,671', '$446,671', '$446,671', false],
          ['Net Operating Income (Pre-Tax)', '$947,761', '$2,416,184', '$4,404,596', true],
        ] : [
          ['Total Revenue', '$2,850,000', '$4,000,000', '$6,250,000', false],
          ['Cost of Sales', '$1,380,000', '$1,618,000', '$2,100,000', false],
          ['Gross Profit', '$1,470,000', '$2,382,000', '$4,150,000', true],
          ['Operating Expenses', '$868,425', '$1,422,000', '$2,223,000', false],
          ['Operating Profit', '$601,575', '$960,000', '$1,927,000', true],
          ...(cfg.id === 'northsam' ? [['Multi-Tenant Rental Income (Plan B)', '$300,000', '$360,000', '$420,000', false]] : []),
          ['SBA Loan Interest', cfg.id === 'humble' ? '$356,000' : '$490,000', cfg.id === 'humble' ? '$352,000' : '$485,000', cfg.id === 'humble' ? '$347,000' : '$478,000', false],
          ['Income Taxes (21%)', '$51,571', '$127,680', '$331,800', false],
          ['Net Profit', '$194,004', '$480,320', '$1,248,200', true],
        ]).map(([metric, y1, y2, y3, bold], i) => (
          <View key={i} style={{ ...s.tRow, ...(bold ? { backgroundColor: '#ECFDF5' } : i % 2 ? s.tRowAlt : {}) }}>
            <Text style={{ ...s.tCell, flex: 2, fontWeight: bold ? 'bold' : 'normal' }}>{metric}</Text>
            <Text style={{ ...s.tCell, fontWeight: bold ? 'bold' : 'normal' }}>{y1}</Text>
            <Text style={{ ...s.tCell, fontWeight: bold ? 'bold' : 'normal' }}>{y2}</Text>
            <Text style={{ ...s.tCell, fontWeight: bold ? 'bold' : 'normal', color: bold ? c.success : c.dark }}>{y3}</Text>
          </View>
        ))}
      </View>
      <View style={{ flexDirection: 'row', gap: 8 }}>
        {[{ v: cfg.dscr.y1, l: 'Y1 DSCR' }, { v: cfg.dscr.y2, l: 'Y2 DSCR' }, { v: cfg.dscr.y3, l: 'Y3 DSCR' }, { v: '$1.92M', l: '3-Year Net Profit' }].map((m, i) => (
          <View key={i} style={{ ...s.metric, flex: 1, backgroundColor: i < 3 ? '#ECFDF5' : '#EFF6FF' }}>
            <Text style={{ ...s.metricVal, color: c.success }}>{m.v}</Text>
            <Text style={s.metricLbl}>{m.l}</Text>
          </View>
        ))}
      </View>
      <Footer />
    </Page>

    {/* ==================== 8b. FINANCIAL PROJECTIONS (P2) ==================== */}
    <Page size="A4" style={s.page}>
      <Header title="Financial Projections (cont.)" planLabel={planLabel} />
      <Text style={s.title}>Cost Structure & Personnel</Text>
      <Text style={s.subtitle}>Operating Expense Breakdown</Text>
      <View style={{ borderWidth: 1, borderColor: c.border, borderRadius: 4, marginBottom: 12 }}>
        <View style={s.tHead}>
          <Text style={{ ...s.tHeadCell, flex: 2 }}>Category</Text>
          <Text style={s.tHeadCell}>Year 1</Text><Text style={s.tHeadCell}>Year 2</Text><Text style={s.tHeadCell}>Year 3</Text>
        </View>
        {[
          ['Personnel (CEO + Staff + Benefits)', '$570,025', '$950,000', '$1,450,000'],
          ['Marketing (Ads, Shows, Billboards)', '$120,000', '$180,000', '$350,000'],
          ['Building (Taxes, Utilities, Maint.)', '$99,000', '$112,000', '$123,000'],
          ['G&A (Legal, Insurance, Software)', '$79,400', '$180,000', '$300,000'],
        ].map(([cat, y1, y2, y3], i) => (
          <View key={i} style={{ ...s.tRow, ...(i % 2 ? s.tRowAlt : {}) }}>
            <Text style={{ ...s.tCell, flex: 2 }}>{cat}</Text>
            <Text style={s.tCell}>{y1}</Text><Text style={s.tCell}>{y2}</Text><Text style={s.tCell}>{y3}</Text>
          </View>
        ))}
        <View style={{ ...s.tRow, backgroundColor: '#E2EFDA' }}>
          <Text style={{ ...s.tCell, flex: 2, fontWeight: 'bold' }}>TOTAL OPERATING EXP.</Text>
          <Text style={{ ...s.tCell, fontWeight: 'bold' }}>$868,425</Text>
          <Text style={{ ...s.tCell, fontWeight: 'bold' }}>$1,422,000</Text>
          <Text style={{ ...s.tCell, fontWeight: 'bold' }}>$2,223,000</Text>
        </View>
      </View>
      <Text style={s.subtitle}>Year 1 Personnel Detail ($570,025)</Text>
      <View style={{ borderWidth: 1, borderColor: c.border, borderRadius: 4, marginBottom: 12 }}>
        <View style={s.tHead}>
          <Text style={{ ...s.tHeadCell, flex: 3 }}>Position</Text><Text style={s.tHeadCell}>Annual Salary</Text>
        </View>
        {[
          ['CEO / Founder', '$125,000'], ['Office Manager / EA', '$55,000'], ['Bookkeeper', '$50,000'],
          ['Warehouse Logistics Coordinator', '$46,000'], ['Sales Assistant', '$40,000'],
          ['Sales Rep — Water (+15% commission)', '$42,500'], ['Sales Rep — Janitorial (+15%)', '$42,500'],
          ['Sales Rep — Medical Devices (+15%)', '$42,500'], ['Full Stack Developer ($5K/mo)', '$60,000'],
          ['Employee Benefits (~15%)', '$66,525'],
        ].map(([pos, sal], i) => (
          <View key={i} style={{ ...s.tRow, ...(i % 2 ? s.tRowAlt : {}) }}>
            <Text style={{ ...s.tCell, flex: 3 }}>{pos}</Text>
            <Text style={{ ...s.tCell, fontWeight: 'bold' }}>{sal}</Text>
          </View>
        ))}
      </View>
      <View style={s.greenBox}>
        <Text style={{ fontSize: 9, color: c.dark }}>
          <Text style={{ fontWeight: 'bold' }}>CEO at fair market salary ($125K).</Text> Year 1 is profitable with $194K net income. All sales reps include 15% commission structure tied to performance.{cfg.id === 'northsam' ? ' Plan B layers an additional $300K–$450K of multi-tenant rental income on top of operating profit.' : cfg.id === 'ronan' ? ' Plan C is owner-occupied — Year-1 Net Operating Income lands at $948K on the seven diversified revenue streams, anchored by our flagship Backorder Sourcing engine (Sterile Mate\u2019s 6-year benchmark and door-opener into every Texas hospital system, now automated by AskIsaac), and scales to $4.25M by Year 3.' : ''}
        </Text>
      </View>
      <Footer />
    </Page>

    {/* ==================== 9. USE OF FUNDS ==================== */}
    <Page size="A4" style={s.page}>
      <Header title="Use of Funds" planLabel={planLabel} />
      <Text style={s.title}>Use of Funds — {cfg.totalLabel}</Text>
      <Text style={s.subtitle}>SBA 7(a) Capital Deployment</Text>
      <View style={{ borderWidth: 1, borderColor: c.border, borderRadius: 4, marginBottom: 15 }}>
        <View style={s.tHead}>
          <Text style={{ ...s.tHeadCell, flex: 2 }}>Category</Text>
          <Text style={s.tHeadCell}>Amount</Text><Text style={s.tHeadCell}>%</Text><Text style={{ ...s.tHeadCell, flex: 2 }}>Description</Text>
        </View>
        {cfg.useOfFunds.map(([cat, amt, pct, desc], i) => (
          <View key={i} style={{ ...s.tRow, ...(i % 2 ? s.tRowAlt : {}) }}>
            <Text style={{ ...s.tCell, flex: 2, fontWeight: 'bold' }}>{cat}</Text>
            <Text style={{ ...s.tCell, color: c.primary, fontWeight: 'bold' }}>{amt}</Text>
            <Text style={s.tCell}>{pct}</Text>
            <Text style={{ ...s.tCell, flex: 2 }}>{desc}</Text>
          </View>
        ))}
        <View style={{ ...s.tRow, backgroundColor: '#E2EFDA' }}>
          <Text style={{ ...s.tCell, flex: 2, fontWeight: 'bold' }}>TOTAL</Text>
          <Text style={{ ...s.tCell, fontWeight: 'bold', color: c.primary }}>{cfg.totalLabel}</Text>
          <Text style={{ ...s.tCell, fontWeight: 'bold' }}>100%</Text>
          <Text style={{ ...s.tCell, flex: 2 }}></Text>
        </View>
      </View>
      <Text style={s.subtitle}>Why This Building?</Text>
      {cfg.locationHighlights.map((h, i) => (
        <Bullet key={i}>{h}</Bullet>
      ))}
      <View style={{ ...s.quoteBox, marginTop: 12 }}>
        <Text style={{ fontSize: 9, color: c.dark, lineHeight: 1.4 }}>
          <Text style={{ fontWeight: 'bold' }}>Equity Injection: </Text>{cfg.equityNote.replace('Equity Injection: ', '')}
        </Text>
      </View>
      <Footer />
    </Page>

    {/* ==================== 10. LOAN STRUCTURE & DSCR ==================== */}
    <Page size="A4" style={s.page}>
      <Header title="Loan Structure" planLabel={planLabel} />
      <Text style={s.title}>SBA Loan Structure & DSCR</Text>
      <View style={{ flexDirection: 'row', gap: 10, marginBottom: 15 }}>
        <View style={{ ...s.card, flex: 1, borderTopWidth: 3, borderTopColor: c.primary }}>
          <Text style={s.cardTitle}>SBA 7(a) Loan</Text>
          <Text style={{ ...s.cardText, marginBottom: 4 }}>Amount: {cfg.totalLabel}</Text>
          <Text style={{ ...s.cardText, marginBottom: 4 }}>Rate: Prime + 2.75% (~10.5%)</Text>
          <Text style={{ ...s.cardText, marginBottom: 4 }}>Term: 25 years (real estate)</Text>
          <Text style={{ ...s.cardText }}>Monthly Payment: {cfg.monthlyDebt}</Text>
        </View>
        <View style={{ ...s.card, flex: 1, borderTopWidth: 3, borderTopColor: c.success }}>
          <Text style={s.cardTitle}>Debt Service Coverage</Text>
          <Text style={{ fontSize: 9, color: c.muted, marginBottom: 6 }}>SBA requires minimum 1.15-1.25x</Text>
          <Text style={{ fontSize: 14, fontWeight: 'bold', color: c.success }}>Year 1: {cfg.dscr.y1}</Text>
          <Text style={{ fontSize: 14, fontWeight: 'bold', color: c.success, marginTop: 2 }}>Year 2: {cfg.dscr.y2}</Text>
          <Text style={{ fontSize: 14, fontWeight: 'bold', color: c.success, marginTop: 2 }}>Year 3: {cfg.dscr.y3}</Text>
        </View>
      </View>
      <Text style={s.subtitle}>Annual Debt Service Analysis</Text>
      <View style={{ borderWidth: 1, borderColor: c.border, borderRadius: 4, marginBottom: 15 }}>
        <View style={s.tHead}>
          <Text style={{ ...s.tHeadCell, flex: 2 }}>Metric</Text>
          <Text style={s.tHeadCell}>Year 1</Text><Text style={s.tHeadCell}>Year 2</Text><Text style={s.tHeadCell}>Year 3</Text>
        </View>
        {(cfg.id === 'ronan' ? [
          ['Annual Debt Service (SBA P&I + Existing)', cfg.annualDebt, cfg.annualDebt, cfg.annualDebt],
          ['Net Operating Income (Pre-Tax)', '$947,761', '$2,416,184', '$4,404,596'],
          ['NOI + Add-Back Debt Service (for DSCR)', '$1,394,432', '$2,862,855', '$4,851,267'],
          ['DSCR', cfg.dscr.y1, cfg.dscr.y2, cfg.dscr.y3],
        ] : [
          ['Annual Debt Service (P&I)', cfg.annualDebt, cfg.annualDebt, cfg.annualDebt],
          ['Operating Profit', '$601,575', '$960,000', '$1,927,000'],
          ...(cfg.id === 'northsam' ? [['Multi-Tenant Rental Income', '$300,000', '$360,000', '$420,000']] : []),
          ['DSCR', cfg.dscr.y1, cfg.dscr.y2, cfg.dscr.y3],
          ['Net Profit After Tax', '$194,004', '$480,320', '$1,248,200'],
        ]).map(([metric, y1, y2, y3], i, arr) => {
          const dscrIdx = arr.findIndex((r) => r[0] === 'DSCR');
          const isDSCR = i === dscrIdx;
          return (
            <View key={i} style={{ ...s.tRow, ...(isDSCR ? { backgroundColor: '#ECFDF5' } : i % 2 ? s.tRowAlt : {}) }}>
              <Text style={{ ...s.tCell, flex: 2, fontWeight: isDSCR ? 'bold' : 'normal' }}>{metric}</Text>
              <Text style={{ ...s.tCell, fontWeight: isDSCR ? 'bold' : 'normal', color: isDSCR ? c.success : c.dark }}>{y1}</Text>
              <Text style={{ ...s.tCell, fontWeight: isDSCR ? 'bold' : 'normal', color: isDSCR ? c.success : c.dark }}>{y2}</Text>
              <Text style={{ ...s.tCell, fontWeight: isDSCR ? 'bold' : 'normal', color: isDSCR ? c.success : c.dark }}>{y3}</Text>
            </View>
          );
        })}
      </View>
      <Text style={s.subtitle}>Exit Strategy Options</Text>
      <View style={{ flexDirection: 'row', gap: 8 }}>
        {[
          { t: 'Strategic Acquisition', tl: '3-5 Years', d: 'Large national distributors actively acquiring in this space', m: '4-6x Revenue' },
          { t: 'Private Equity Rollup', tl: '4-6 Years', d: 'PE firms consolidating healthcare services', m: '5-8x EBITDA' },
          { t: 'Dividend Distributions', tl: 'Ongoing', d: 'Strong margins enable profit distributions', m: '8-12% Yield' },
        ].map((e, i) => (
          <View key={i} style={{ ...s.card, flex: 1 }}>
            <Text style={{ ...s.cardTitle, fontSize: 10 }}>{e.t}</Text>
            <Text style={{ fontSize: 8, color: c.secondary, marginBottom: 3 }}>{e.tl}</Text>
            <Text style={s.cardText}>{e.d}</Text>
            <Text style={{ fontSize: 9, fontWeight: 'bold', color: c.success, marginTop: 4 }}>{e.m}</Text>
          </View>
        ))}
      </View>
      <Footer />
    </Page>

    {/* ==================== 11. RISK FACTORS ==================== */}
    <Page size="A4" style={s.page}>
      <Header title="Risk Factors" planLabel={planLabel} />
      <Text style={s.title}>Risk Factors & Mitigations</Text>
      <Text style={s.subtitle}>Transparency builds trust</Text>
      {[
        { r: 'Market Competition', d: 'Large national distributors may enter our niche markets', m: 'Exclusive partnerships and custom solutions create barriers to entry' },
        { r: 'Key Person Dependency', d: 'Business relies heavily on founder relationships', m: 'Building sales team and systematizing processes with investment' },
        { r: 'Partnership Concentration', d: 'Revenue dependent on key supplier partnerships', m: 'Diversifying across 4 strategic partners and adding new verticals' },
        { r: 'Healthcare Regulatory Changes', d: 'Compliance requirements may shift', m: 'Strong relationships with VA and hospital compliance teams' },
        { r: 'Sales Cycle Length', d: 'Healthcare procurement can be lengthy', m: 'Existing vendor relationships reduce cycle to 90 days (vs 180+ avg)' },
        { r: 'Real Estate Market Risk', d: 'Property values may fluctuate', m: cfg.id === 'humble' ? 'Owner-occupied building with direct revenue generation. Location on US-59 with 168K daily traffic.' : cfg.id === 'ronan' ? 'Owner-occupied Class-A asset on 5.08 acres at Beltway 8 + I-45 (200K+ daily traffic). Seven diversified operating revenue streams — anchored by the AskIsaac-automated Backorder Sourcing engine (Sterile Mate\u2019s 6-year benchmark, scaling from $1.2M Y1 to $3.2M Y3) plus Water, Medical Devices, Healthcare Construction, Private-Label Gloves direct-import, Private-Label Microfiber, and BioBurden Cleaning — generate $4.54M Y1 → $11.64M Y3, no third-party rental dependency required to service the SBA 504 stack.' : 'Owner-occupied multi-tenant Class-B asset on Beltway 8 with 180K daily traffic. Multi-tenant lease income provides additional cash-flow buffer.' },
      ].map((item, i) => (
        <View key={i} style={{ ...s.card, borderLeftWidth: 3, borderLeftColor: c.warning }}>
          <Text style={{ ...s.cardTitle, color: c.warning, fontSize: 10 }}>{item.r}</Text>
          <Text style={s.cardText}>{item.d}</Text>
          <Text style={{ ...s.cardText, marginTop: 4, fontWeight: 'bold', color: c.success }}>Mitigation: {item.m}</Text>
        </View>
      ))}
      <Footer />
    </Page>

    {/* ==================== 12. FAQ ==================== */}
    <Page size="A4" style={s.page}>
      <Header title="FAQ" planLabel={planLabel} />
      <Text style={s.title}>Frequently Asked Questions</Text>
      {[
        { q: 'What is the investment amount and structure?', a: cfg.id === 'humble'
            ? 'The total project cost is $3,415,000 via SBA 7(a) financing, covering building acquisition ($2.1M), renovations ($150K), equipment ($115K), inventory ($100K), and 12-month working capital ($950K including $120K marketing budget). Equity injection is approximately $340K (10%).'
            : cfg.id === 'ronan'
              ? 'The total project cost is $4,490,000 — financed as $3.456M via SBA 504 plus a separate $500K revolving line of credit for operating capital. The SBA 504 loan covers the 22,750 SF Class-A 3-story HQ on 5.08 acres at 16651 Ronan Rd ($3.5M acquisition), a $160K BWS + MEP + RAS renovation program for the 2nd-Level executive layout ($102K 2nd-Level estimate & layout, $6,875 Schematic Design, $9,750 Design Development, $13,875 Construction Documents, $25K MEP Consultant, $2,500 MEP Coordination, RAS Fees & Construction Visitation), and a $180K dedicated statewide B2B marketing launch. Equity injection is approximately $384K (10% of the $3.84M SBA-financed portion). Plan C is owner-occupied across all three floors — seven diversified revenue streams (Backorder Sourcing via AskIsaac — our 6-year benchmark and flagship, Water Purification & Healthcare Capital Projects, Medical Device Sales, Healthcare Construction, Sterile Mate Private-Label Exam Gloves direct-import, Private-Label Microfiber, and Specialized BioBurden Cleaning) drive $4.54M Y1 → $7.59M Y2 → $11.64M Y3 with a Year-2 DSCR of 6.71x — substantially above SBA 504 1.15x\u20131.25x minimums.'
              : 'The total project cost is $4,700,000 via SBA 7(a) financing, covering acquisition of the 87,282 SF "2SIXTY3" Class-B campus ($3.4M), renovation & capital improvements ($800K), and operating capital / working reserves ($500K). Equity injection is approximately $470K (10%).' },
        { q: 'What is the expected timeline to profitability?', a: `We are already profitable. Year 1 projects $194K net income with DSCR of ${cfg.dscr.y1}. By Year 3, net income reaches $1.25M with DSCR of ${cfg.dscr.y3}.` },
        { q: 'What makes Sterile Mate different from competitors?', a: "AskIsaac is not a CRM or ticketing system. It's a front-line command network that deploys when critical environments need help. We built a two-sided marketplace with network effects — our 30-second response time is something enterprise software can't match." },
        { q: 'Why should I believe you can execute?', a: "We're operators, not outsiders. 6 years of trust in the Texas Medical Center. We've fixed critical water systems while surgeries waited. One of the largest public health systems in the U.S. is using AskIsaac TODAY." },
        { q: 'What is the exit strategy?', a: 'Multiple paths: strategic acquisition by healthcare/tech companies at $30M+ valuation, private equity rollup, or continued growth with recurring revenue and dividend distributions.' },
        { q: 'How are the revenue streams differentiated?', a: cfg.id === 'ronan'
            ? `Plan C is owner-occupied and runs on seven diversified operating revenue streams anchored by our flagship Backorder Sourcing engine (AskIsaac-automated \u00b7 6-yr Sterile Mate benchmark and door-opener into every Texas hospital system, $1.2M Y1 → $3.2M Y3). Supporting streams: Water Purification & Healthcare Capital Projects ($1.1M Y1), Medical Device Sales ($800K Y1), Healthcare Construction / GC ($400K Y1), Sterile Mate Private-Label Exam Gloves — direct import from Haiphong Vietnam, 3.5-mil + 4.0-mil nitrile, 200 pcs/box (2,000 gloves per master case), scaling from 8 containers/9,040 cartons ($749K Y1) to 28 containers/31,560 cartons ($2.79M Y3) with 37.8%\u201341.4% gross margins, Private-Label Microfiber (Texas MF, $92.5K Y1), and Specialized BioBurden Cleaning Service across 5 hospitals ($200K Y1). Total Y1 revenue $4.54M scales to $11.64M by Year 3 — no third-party rental income is required.`
            : `B2B Healthcare (existing contracts, Day 1 revenue), Retail Medical Supply (The Supply Doctor DBA, foot traffic from ${cfg.id === 'humble' ? '168K' : '200K+'} daily vehicles), and DME/Insurance (Phase II Medicare billing). Each serves a different customer segment from the same HQ${cfg.id === 'northsam' ? ', and surplus floor area is leased multi-tenant for ancillary income' : ''}.` },
      ].map((item, i) => (
        <View key={i} style={{ marginBottom: 10 }}>
          <Text style={{ fontSize: 10, fontWeight: 'bold', color: c.primary, marginBottom: 3 }}>Q: {item.q}</Text>
          <Text style={{ fontSize: 9, color: c.dark, lineHeight: 1.4 }}>{item.a}</Text>
        </View>
      ))}
      <Footer />
    </Page>

    {/* ==================== 13. CONTACT ==================== */}
    <Page size="A4" style={s.page}>
      <Header title="Contact" planLabel={planLabel} />
      <Text style={s.title}>Contact Information</Text>
      <View style={{ padding: 25, backgroundColor: c.light, borderRadius: 8, marginVertical: 20 }}>
        <Text style={{ fontSize: 16, fontWeight: 'bold', color: c.primary, marginBottom: 4, textAlign: 'center' }}>George Broughton III</Text>
        <Text style={{ fontSize: 11, color: c.secondary, marginBottom: 15, textAlign: 'center' }}>Founder & CEO, Sterile Mate LLC</Text>
        <View style={{ marginBottom: 10 }}>
          <Text style={{ fontSize: 9, color: c.muted, marginBottom: 3 }}>Email</Text>
          <Text style={{ fontSize: 12, color: c.primary }}>george.broughton@sterilemate.com</Text>
        </View>
        <View style={{ marginBottom: 10 }}>
          <Text style={{ fontSize: 9, color: c.muted, marginBottom: 3 }}>Website</Text>
          <Text style={{ fontSize: 12, color: c.primary }}>https://sterilemate.com</Text>
        </View>
        <View style={{ marginBottom: 10 }}>
          <Text style={{ fontSize: 9, color: c.muted, marginBottom: 3 }}>Proposed HQ Location</Text>
          <Text style={{ fontSize: 11, color: c.dark }}>{cfg.address}</Text>
        </View>
        <View>
          <Text style={{ fontSize: 9, color: c.muted, marginBottom: 3 }}>Current Corporate Address</Text>
          <Text style={{ fontSize: 11, color: c.dark }}>396 W Greens Road, Suite 1139, Houston, TX 77067</Text>
        </View>
      </View>
      <View style={s.quoteBox}>
        <Text style={{ fontSize: 10, fontStyle: 'italic', color: c.dark, lineHeight: 1.5 }}>
          "I built this company because I saw hospitals struggling to get what they needed, when they needed it. Now we're building a headquarters to serve them even better — combining B2B operations, retail medical supply, and DME under one roof."
        </Text>
        <Text style={{ fontSize: 9, color: c.muted, marginTop: 6, textAlign: 'right' }}>— George Broughton III</Text>
      </View>
      <View style={{ marginTop: 20, padding: 15, backgroundColor: c.primary, borderRadius: 8, alignItems: 'center' }}>
        <Text style={{ fontSize: 12, color: '#FFFFFF', fontWeight: 'bold' }}>Sterile Mate LLC — Powered by AskIsaac Technology</Text>
        <Text style={{ fontSize: 9, color: c.accent, marginTop: 4 }}>6 Years Serving Healthcare | $3.9M+ Cumulative Revenue | HUB Certified</Text>
      </View>
      <Footer />
    </Page>
  </Document>
  );
};

// Helper exposed so the portal page can dynamically pick the correct filename per scenario
export const getInvestorPackageFileName = (scenario = 'humble') =>
  (SCENARIOS[scenario] || SCENARIOS.ronan).fileName;

export default InvestorPackagePDF;
